import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseDateRangePicker from "@/components_v2/FormFields/DatePicker/BaseDateRangePicker";
import BaseDropdown from "@/components_v2/FormFields/Dropdown/BaseDropdown";
import type { AnalyticsInterval } from "@/redux/certifications/types";
import {
  ANALYTICS_INTERVALS,
  DEFAULT_FILTERS,
} from "@/features/certifications/analytics/constants";
import { getTrackLabel } from "@/features/certifications/analytics/utils";
import { AnalyticsFiltersProps } from "@/features/certifications/analytics/types";

import "../styles.css";

const AnalyticsFilters = ({
  certification,
  filters,
  onChange,
  periodError,
}: AnalyticsFiltersProps) => {
  const { t } = useTranslation();

  const trackOptions = useMemo(
    () =>
      [...certification.tracks]
        .sort((a, b) => a.order - b.order)
        .map((track) => ({
          value: track.id,
          label: getTrackLabel(t, certification, track.id, track.title),
        })),
    [certification, t],
  );

  const intervalOptions = useMemo(
    () =>
      ANALYTICS_INTERVALS.map((interval) => ({
        value: interval,
        label: t(`certification.admin.analytics.filters.intervals.${interval}`),
      })),
    [t],
  );

  const range =
    filters.startDate && filters.endDate
      ? ([
          DateTime.fromISO(filters.startDate),
          DateTime.fromISO(filters.endDate),
        ] as [DateTime, DateTime])
      : null;

  const isDefault =
    filters.track === DEFAULT_FILTERS.track &&
    filters.startDate === DEFAULT_FILTERS.startDate &&
    filters.endDate === DEFAULT_FILTERS.endDate &&
    filters.interval === DEFAULT_FILTERS.interval;

  return (
    <div className="flex flex-col gap-200">
      <div className="analytics-filters flex flex-wrap items-end justify-end gap-300">
        <label className="analytics-filter flex flex-col gap-100 body-s-regular">
          {t("certification.admin.analytics.filters.path")}

          <BaseDropdown
            value={filters.track}
            options={trackOptions}
            placeholder={t("certification.admin.analytics.filters.allPaths")}
            allowClear
            showError={false}
            onChange={(track?: number) => onChange({ ...filters, track })}
          />
        </label>

        <label className="analytics-filter analytics-filter-period flex flex-col gap-100 body-s-regular">
          {t("certification.admin.analytics.filters.period")}

          <BaseDateRangePicker
            value={range}
            allowClear
            placeholder={[
              t("certification.admin.analytics.filters.defaultStart"),
              t("certification.admin.analytics.filters.defaultEnd"),
            ]}
            onChange={(value) => {
              const [start, end] = value ?? [];
              onChange({
                ...filters,
                startDate: start?.toISODate() ?? undefined,
                endDate: end?.toISODate() ?? undefined,
              });
            }}
          />
        </label>

        <label className="analytics-filter flex flex-col gap-100 body-s-regular">
          {t("certification.admin.analytics.filters.interval")}

          <BaseDropdown
            value={filters.interval}
            options={intervalOptions}
            showError={false}
            onChange={(interval: AnalyticsInterval) =>
              onChange({ ...filters, interval })
            }
          />
        </label>

        {!isDefault && (
          <BaseButton
            variant={BUTTON_VARIANTS.LINK}
            size={BUTTON_SIZES.SMALL}
            onClick={() => onChange(DEFAULT_FILTERS)}
          >
            {t("certification.admin.analytics.filters.reset")}
          </BaseButton>
        )}
      </div>

      {periodError && (
        <p
          className="analytics-alert analytics-period-error body-s-regular"
          role="alert"
        >
          {periodError}
        </p>
      )}
    </div>
  );
};

export default AnalyticsFilters;
